import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import {
  initGA,
  setUserId,
  setUserProperties,
  trackEvent,
  trackPageView,
  trackAppOpen,
  GA4EventParams,
} from 'utils/analytics';
import { deriveUserProperties } from 'utils/analyticsHelpers';
import { useStorage } from './StorageProvider';

interface AnalyticsContextValue {
  trackEvent: (eventName: string, params?: GA4EventParams) => void;
  trackPageView: (path: string, title?: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID ?? '';

/** Reverse-geocode lat/lng to a city name via Nominatim */
async function getCityFromCoords(lat: number, lng: number): Promise<string | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    return (
      data?.address?.city ??
      data?.address?.town ??
      data?.address?.village ??
      data?.address?.county ??
      null
    );
  } catch {
    return null;
  }
}

/** Ask browser for current position and resolve to a city name */
function resolveCurrentCity(): Promise<string | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const city = await getCityFromCoords(
          position.coords.latitude,
          position.coords.longitude,
        );
        resolve(city);
      },
      () => resolve(null), // user denied or error — fail silently
      { timeout: 5000 },
    );
  });
}

/** Read UTM params from the current URL for campaign attribution */
function readUtmParams(): { source?: string; campaign?: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') ?? undefined,
    campaign: params.get('utm_campaign') ?? undefined,
  };
}

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userProfileData } = useStorage();
  const resolvedCityRef = useRef<string | null>(null);

  // Initialize GA4, fire app_open with UTM attribution, and resolve city — all on mount
  useEffect(() => {
    initGA(MEASUREMENT_ID);

    // Fire app_open with UTM campaign params for marketing attribution
    const { source, campaign } = readUtmParams();
    trackAppOpen(source, campaign);

    // Resolve current city in background — doesn't block anything
    resolveCurrentCity().then((city) => {
      resolvedCityRef.current = city;
      // Re-sync user properties now that we have the city
      if (userProfileData?.id) {
        setUserId(String(userProfileData.id));
        setUserProperties(deriveUserProperties(userProfileData, city));
      }
    });

    // Sync immediately on mount for returning users (before city resolves)
    if (userProfileData?.id) {
      setUserId(String(userProfileData.id));
      setUserProperties(deriveUserProperties(userProfileData, null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-sync whenever profile changes (login / logout / profile update)
  useEffect(() => {
    if (!userProfileData) {
      setUserId(null);
      return;
    }
    if (userProfileData?.id) {
      setUserId(String(userProfileData.id));
      // Use already-resolved city if available
      setUserProperties(deriveUserProperties(userProfileData, resolvedCityRef.current));
    }
  }, [userProfileData]);

  return (
    <AnalyticsContext.Provider value={{ trackEvent, trackPageView }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
